import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory, getCategoryBySlug } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'rexue');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const category = useMemo(() => getCategoryBySlug(activeSlug), [activeSlug]);
  const comics = useMemo(() => getComicsByCategory(activeSlug), [activeSlug]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category?.name || ''}漫画 - 洞洞杂货铺漫画`,
    description: `在线免费阅读${category?.name || ''}漫画，尽在36漫画网洞洞杂货铺。海量${category?.name || ''}漫画每日更新，高清画质，流畅阅读。`,
    url: `https://36manhua.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '36漫画网',
      url: 'https://36manhua.com',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200/70">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '分类浏览', href: '/category/rexue' },
                  { label: `${category?.name || ''}漫画` },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mt-4">
                {category?.name}漫画
              </h1>
              <p className="text-sm text-foreground-500 mt-2">
                共 {comics.length} 部{category?.name}类漫画，每日更新，免费阅读
              </p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>

            {comics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-inbox-line text-3xl"></i>
                </div>
                <p className="text-foreground-500 mt-4">暂无该分类的漫画</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}